<script lang="ts">
    import { onMount } from "svelte";

    import SVGIcon from "$/components/SVGIcon.svelte";

    import { ActiveTopicStore, ActiveTopic } from "$/stores/extension/ActiveTopicStore";
    import { TokenStore } from "$/stores/TokenStore";
    import {
        checkAndGenerateSocialMediaURL,
        generateBase64ImageFromUrl,
        toggleSocialMediaPostSaveState,
    } from "$/utils/Utils";
    import { AppStore } from "$/stores/AppStore";
    import { v4 as uuid } from "uuid";
    import {ExtensionStore} from "$/stores/ExtensionStore";

    let saveButton: HTMLButtonElement;
    let observer: MutationObserver;
    let initialLoad = false;

    export let editRole: number;

    // function to create the "Save Post" button
    const createSaveButton = (element: HTMLElement) => {
        const postSaveButton = saveButton.cloneNode(true) as HTMLButtonElement;

        postSaveButton.classList.remove("cw-save-hide");
        postSaveButton.addEventListener("click", async () => {
            toggleSocialMediaPostSaveState(postSaveButton, true);

            const post = await extractPostData(element);

            let url = generateLinkedinPostUrl(post.id as String);

            let snippet = {
                id: uuid(),
                content: post.postContent ?? "No content",
                nameSuggestion: post.name?.length
                    ? `${post.name} on LinkedIn`
                    : "LinkedIn Post",
                highlight: {},
                comments: [],
                url: checkAndGenerateSocialMediaURL("linkedin", url),
                createdBy: $TokenStore?.user,
                createdOn: new Date().getTime(),
                author: {
                    name: post.name,
                    username: post.username,
                    url: post.authorUrl,
                    image: post.image,
                },
            };

            ExtensionStore.setOpened(true);

            ActiveTopicStore.createSnippet(snippet);

            // set focus on the newly created snippet
            AppStore.setFocused({ snippetId: snippet.id });

            // hide the loading spinner
            toggleSocialMediaPostSaveState(postSaveButton, false);
        });

        return postSaveButton;
    };

    const filterPostContent = (postContent) => {
        // Create a DocumentFragment to isolate parsing
        const documentFragment = document.createDocumentFragment();
        documentFragment.appendChild(postContent.cloneNode(true));
        
        const postElement = documentFragment.children[0] as HTMLElement;

        // Remove visually hidden text or "more..." buttons
        const elementsToRemove = postElement.querySelectorAll(
            ".visually-hidden, .feed-shared-inline-show-more-text__see-more-less-toggle",
        );
        elementsToRemove.forEach((element) => element.remove());

        // Return the HTML as a string without browser corrections
        const serializer = new XMLSerializer();
        
        return addWindowLocationToLinks(serializer.serializeToString(postElement));
    };

    /* Currently LinkedIn has a bug where they nest an <a> tag with an href starting with "/in/"
     * inside another <a> tag. This happens only when the post contains a link to a LinkedIn profile.
     * That causes the links not to work correctly when saved as snippets because automatic HTML5 serialization.
     * This function will prepend window.location.href to the inner link's href so that it works correctly.
     */
     const addWindowLocationToLinks = (htmlString) => {
        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = htmlString;

        // Find all <a> tags with href starting with "/in/"
        const innerLinks = tempDiv.querySelectorAll("a[href^='/in/']") as NodeListOf<HTMLAnchorElement>;

        innerLinks.forEach((innerLink) => {
            // Prepend window.location.origin to the href
            innerLink.href = `${window.location.origin}${innerLink.getAttribute("href")}`;
        });

        return tempDiv.innerHTML;
    };

    const getImage = async (element: HTMLElement) => {
        const src = element.querySelector("img")?.getAttribute("src");

        try {
            const base64ImgSrc = await generateBase64ImageFromUrl(src);
            return base64ImgSrc;
        } catch (error) {
            console.error("Failed to generate base64 image:", error);
            return "";
        }
    };

    const getUsername = (element: HTMLElement) => {
        const href = element.getAttribute("href");

        // Example URL that we get from element: https://www.linkedin.com/in/pascalbornet?miniProfileUrn=urn%3Ali%3Afsd_profile%3AACoAA
        // need to username from url for user or company or showcase profile
        const pattern = /linkedin\.com\/(?:in|company|showcase)\/([^?\/]+)/;
        const match = href.match(pattern);

        return match && match[1] ? match[1] : "";
    };

    const extractPostData = async (element: HTMLElement) => {
        const postContent = filterPostContent(
            element.querySelector(".feed-shared-update-v2__description")
        );
        const name =
            element
                .querySelector(".update-components-actor__title")
                ?.firstElementChild.firstElementChild.firstElementChild.textContent?.trim() ||
            null;

        let image: string | null = null;
        let username: string | null = null;
        let authorUrl: string | null = null;

        const userAvatarElement = element.querySelector(
            ".update-components-actor__image",
        ) as HTMLElement;

        if (userAvatarElement) {
            image = await getImage(userAvatarElement);
            username = getUsername(userAvatarElement);
            authorUrl = userAvatarElement.getAttribute("href");
        }

        return {
            id: element.getAttribute("data-urn"),
            postContent,
            name,
            image,
            username,
            authorUrl,
        };
    };

    const generateLinkedinPostUrl = (urnId: String) => {
        return `https://www.linkedin.com/feed/update/${urnId}/`;
    };

    const addSaveButtonToPosts = async () => {
        const postElements = document.querySelectorAll(
            ".feed-shared-update-v2",
        );

        await Promise.all(
            Array.from(postElements)
                .filter(
                    (element: HTMLElement) =>
                        !element.querySelector(".cw-save-post-div"),
                )
                .map(async (element: HTMLElement) => {
                    // check if the post already has a "Save Post" button
                    const saveButton = createSaveButton(element);

                    // find feed-shared-social-actions element and append our button
                    let socialActions = element.querySelector(
                        ".feed-shared-social-action-bar",
                    );

                    // create div with classes feed-shared-social-action-bar__action-button
                    // also add our cw-save-post-div class so we can find it later if needed for removal
                    let feedSharedSocialDiv = document.createElement("div");
                    feedSharedSocialDiv.classList.add(
                        "cw-save-post-div",
                        "feed-shared-social-action-bar__action-button",
                    );

                    // create span with classes artdeco-hoverable-trigger artdeco-hoverable-trigger--content-placed-top artdeco-hoverable-trigger--is-hoverable artdeco-hoverable-trigger--mouse-in-content ember-view
                    let artdecoSpan = document.createElement("span");
                    artdecoSpan.classList.add(
                        "artdeco-hoverable-trigger",
                        "artdeco-hoverable-trigger--content-placed-top",
                        "artdeco-hoverable-trigger--is-hoverable",
                        "artdeco-hoverable-trigger--mouse-in-content",
                        "ember-view",
                    );

                    // append our button to the span then append the span to the div then append the div to the socialActions
                    artdecoSpan.appendChild(saveButton);
                    feedSharedSocialDiv.appendChild(artdecoSpan);
                    socialActions?.appendChild(feedSharedSocialDiv);
                }),
        );
    };

    // function to check the current URL and add Save Post buttons if needed
    const checkAndAddSaveButtons = () => {
        const currentUrl = window.location.href;
        if (
            currentUrl.includes("linkedin.com") &&
            !currentUrl.includes("messaging") &&
            !currentUrl.includes("notifications")
        ) {
            if (!document.querySelector(".feed-shared-update-v2")) {
                requestAnimationFrame(() => {
                    addSaveButtonToPosts();
                });
            } else {
                addSaveButtonToPosts();
            }
        }

        // continue observing changes in the document body
        observer?.observe(document.body, {
            attributes: false,
            childList: true,
            subtree: true,
        });
    };

    // function to remove all "Save Post" buttons
    const removeSaveButtons = () => {
        const saveButtons = document.querySelectorAll(".cw-save-post-div");
        saveButtons.forEach((button: HTMLElement) => button.remove());

        // stop observing changes in the document body if observer exists
        observer?.disconnect();
    };

    onMount(() => {
        // create observer listener for new elements with the class "feed-shared-update-v2"
        observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type === "childList" && editRole >= 3) {
                    mutation.addedNodes.forEach((addedNode) => {
                        if (
                            addedNode instanceof HTMLElement &&
                            addedNode.classList.contains(
                                "feed-shared-update-v2",
                            )
                        ) {
                            // if a new "feed-shared-update-v2" element has been added
                            // add a "Save Post" button to it
                            addSaveButtonToPosts();
                        }
                    });
                }
            });
        });

        if (editRole >= 3) checkAndAddSaveButtons();
        initialLoad = true;
    });

    // if user has edit role >= 3 and initial load (component mounted), add "Save Post" buttons else remove them
    $: initialLoad && editRole >= 3
        ? checkAndAddSaveButtons()
        : removeSaveButtons();
</script>

{#if window.location.href.includes("linkedin.com")}
    <button
        class="cw-save-hide cw-linkedin-btn social-actions-button send-privately-button artdeco-button
               artdeco-button--4 artdeco-button--tertiary artdeco-button--muted
               send-privately-button flex-wrap"
        title="Save as Snippet"
        bind:this={saveButton}
    >
        <div class="cw-save__loading-spinner" style="display: none;">
            <SVGIcon
                name="spinner"
                color="#666666"
                height="100%"
                size="100%"
                viewbox="0 0 24 24"
                strokeWidth="2"
            />
        </div>
        <div class="cw-save__icon">
            <SVGIcon
                name="save"
                size={24}
                viewbox="0 0 28 28"
                color="#666666"
                strokeWidth="0.5"
            />
        </div>
        <span
            class="cw-linkedin-text artdeco-button__text social-action-button__text"
            >Save</span
        >
    </button>
{/if}

<style>
    @import "../../extension/external.css";
</style>
